import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { apiFetch } from "../../../utils/api";
import "./WalkInsPage.css";

type BillingBooking = {
  booking_id: string | number;
  guest_name?: string | null;
  guest_email?: string | null;
  guest_phone?: string | null;
  booking_status?: string | null;
  payment_status?: string | null;
  ui_status?: string | null;
  payment_currency?: string | null;
  payment_amount?: string | null;
  total_pax?: number | null;
  headcount?: number | null;
  booking_items_summary?: string | null;
  sites_summary?: string | null;
  first_session_at_local?: string | null;
  last_session_at_local?: string | null;
  participants?: Array<{
    participant_id: string | number;
    participant_name?: string | null;
    first_name?: string | null;
    last_name?: string | null;
  }>;
};

export default function BillingReportPage() {
  const { booking_id } = useParams();
  const nav = useNavigate();

  const [state, setState] = useState<
    | { status: "loading" }
    | { status: "error"; message: string }
    | { status: "ok"; booking: BillingBooking }
  >({ status: "loading" });

  const [paymentMethod, setPaymentMethod] = useState<"cash" | "card_terminal">("cash");
  const [paymentAction, setPaymentAction] = useState<
    | { status: "idle" }
    | { status: "working" }
    | { status: "ok"; message: string }
    | { status: "error"; message: string }
  >({ status: "idle" });

  useEffect(() => {
    let mounted = true;

    async function loadBooking() {
      if (!booking_id) {
        setState({ status: "error", message: "Missing booking_id." });
        return;
      }

      try {
        const res = await apiFetch(`/api/v1/dashboard/bookings/${encodeURIComponent(booking_id)}`, {
          method: "GET",
        });

        const json = await res.json().catch(() => ({}));

        if (!res.ok || json?.ok === false) {
          throw new Error(json?.message || `HTTP ${res.status}`);
        }

        if (mounted) {
          setState({ status: "ok", booking: json.booking });
        }
      } catch (err: any) {
        if (mounted) {
          setState({ status: "error", message: err?.message || "Booking load failed." });
        }
      }
    }

    loadBooking();

    return () => {
      mounted = false;
    };
  }, [booking_id]);

  if (state.status === "loading") {
    return (
      <div className="walkins-page">
        <h1 className="walkins-page-title">Billing Report</h1>
        <div className="walkins-card">Loading booking #{booking_id}…</div>
      </div>
    );
  }

  if (state.status === "error") {
    return (
      <div className="walkins-page">
        <h1 className="walkins-page-title">Billing Report</h1>
        <div className="walkins-card">Failed to load booking: {state.message}</div>
      </div>
    );
  }

  const b = state.booking;
  const customerName = b.guest_name || "Unknown customer";
  const participants = Array.isArray(b.participants) ? b.participants : [];
  const total = Number(b.payment_amount || 0);
  const currency = b.payment_currency || "JOD";

    async function recordPayment() {
    if (!booking_id) return;

    const confirmed = window.confirm(
      `Record ${currency} ${total.toFixed(2)} as ${paymentMethod === "cash" ? "cash" : "card terminal"} payment for booking #${booking_id}?`
    );

    if (!confirmed) return;

    setPaymentAction({ status: "working" });

    try {
      const res = await apiFetch(`/api/v1/dashboard/bookings/${encodeURIComponent(booking_id)}/record-payment`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          payment_method: paymentMethod,
          payment_notes: "Recorded from Billing Report",
        }),
      });

      const json = await res.json().catch(() => ({}));

      if (!res.ok || json?.ok === false) {
        throw new Error(json?.message || `HTTP ${res.status}`);
      }

setState({
  status: "ok",
  booking: {
    ...b,
    ...json?.booking,
    payment_status: json?.booking?.payment_status || "paid",
    booking_status: json?.booking?.booking_status || b.booking_status,
    ui_status: json?.booking?.ui_status || b.ui_status,
  },
});

      setPaymentAction({
        status: "ok",
        message: "Payment recorded. Financial status is now paid. Booking confirmation remains a separate operational decision.",
      });
    } catch (err: any) {
      setPaymentAction({ status: "error", message: err?.message || "Payment failed." });
    }
  }

  return (
    <div className="walkins-page">
      <header className="walkins-page-header">
        <h1 className="walkins-page-title">Billing Report</h1>
        <p className="walkins-page-subtitle">Real booking loaded from AQUORIX booking truth.</p>
      </header>

      <section className="walkins-card">
        <div className="walkins-card-label"><span className="walkins-card-num">1</span> Booking Context</div>
        <div className="walkins-match">
          <div className="walkins-match-avatar">
            {customerName.split(" ").map((p) => p[0]).join("").slice(0, 2).toUpperCase()}
          </div>
          <div className="walkins-match-body">
            <div className="walkins-match-status">Real booking loaded</div>
            <div className="walkins-match-name">{customerName}</div>
            <div className="walkins-match-meta">
              Booking #{b.booking_id} · {b.guest_email || "no email"} · {b.guest_phone || "no phone"}
            </div>
          </div>
          <button type="button" className="walkins-lookup-btn" onClick={() => nav(`/dashboard/bookings/${b.booking_id}`)}>
            View booking
          </button>
        </div>
      </section>

      <section className="walkins-card">
        <div className="walkins-card-label"><span className="walkins-card-num">2</span> Billing Summary</div>
        <div className="walkins-amount-row">
          <div className="walkins-amount-summary">
            Total: <strong>{currency} {total.toFixed(2)}</strong>
            <span style={{ color: "var(--walkins-muted)" }}>
              {" "}· Booking {b.booking_status || "unknown"} · Payment {b.payment_status || "unknown"} · UI {b.ui_status || "unknown"}
            </span>
          </div>
        </div>
      </section>

      <section className="walkins-card">
        <div className="walkins-card-label"><span className="walkins-card-num">3</span> Trip & Participants</div>
        <div className="walkins-status">
          {b.booking_items_summary || "No session summary"} · {b.sites_summary || "No sites"} · {b.total_pax ?? b.headcount ?? 0} divers
        </div>

        {participants.length === 0 ? (
          <div className="walkins-status" style={{ marginTop: 10 }}>No participants found.</div>
        ) : (
          <div className="walkins-session-list" style={{ marginTop: 10 }}>
            {participants.map((p, index) => (
              <div className="billing-charge-row" key={String(p.participant_id || index)}>
                <div className="billing-charge-qty">{index + 1}</div>
                <div>
                  <div className="walkins-session-title">
                    {p.participant_name || `${p.first_name || ""} ${p.last_name || ""}`.trim() || "Unnamed participant"}
                  </div>
                  <div className="walkins-session-sub">Participant #{p.participant_id}</div>
                </div>
              </div>
            ))}
          </div>
        )}
            </section>

      <section className="walkins-card">
        <div className="walkins-card-label"><span className="walkins-card-num">4</span> Record Payment</div>

        <div className="walkins-status">
          This records operator-collected payment only. Stripe payments remain webhook-authoritative.
        </div>

        {paymentAction.status === "ok" ? (
          <div className="walkins-status" style={{ marginTop: 10 }}>
            {paymentAction.message}
          </div>
        ) : null}

        {paymentAction.status === "error" ? (
          <div className="walkins-status" style={{ marginTop: 10 }}>
            Payment failed: {paymentAction.message}
          </div>
        ) : null}

        <div className="walkins-pay-options" style={{ marginTop: 14 }}>
          <label className={`walkins-pay-option ${paymentMethod === "cash" ? "is-selected" : ""}`}>
            <input
              type="radio"
              name="billingReportPaymentMethod"
              checked={paymentMethod === "cash"}
              onChange={() => setPaymentMethod("cash")}
            />
            <div>
              <div className="walkins-pay-title">Cash</div>
              <div className="walkins-pay-sub">Operator collected cash at counter</div>
            </div>
          </label>

          <label className={`walkins-pay-option ${paymentMethod === "card_terminal" ? "is-selected" : ""}`}>
            <input
              type="radio"
              name="billingReportPaymentMethod"
              checked={paymentMethod === "card_terminal"}
              onChange={() => setPaymentMethod("card_terminal")}
            />
            <div>
              <div className="walkins-pay-title">Card terminal</div>
              <div className="walkins-pay-sub">Operator collected payment on external terminal</div>
            </div>
          </label>
        </div>

        <div className="walkins-amount-row">
          <div className="walkins-amount-summary">
            Amount to record: <strong>{currency} {total.toFixed(2)}</strong>
            <span style={{ color: "var(--walkins-muted)" }}>
              {" "}· Current payment status: {b.payment_status || "unknown"}
            </span>
          </div>
        </div>

        <div className="walkins-submit-row">
          <button
            type="button"
            className="walkins-submit"
            onClick={recordPayment}
            disabled={paymentAction.status === "working" || b.payment_status === "paid"}
          >
            {b.payment_status === "paid" ? "Payment already recorded" : paymentAction.status === "working" ? "Recording payment…" : "Record payment"}
          </button>
        </div>
      </section>
    </div>
  );
}
